import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AssessmentOutlined as AssessmentOutlinedIcon,
  DirectionsCarFilledOutlined as DirectionsCarFilledOutlinedIcon,
  EventNoteOutlined as EventNoteOutlinedIcon,
  Groups2Outlined as Groups2OutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  MarkUnreadChatAltOutlined as MarkUnreadChatAltOutlinedIcon,
  NotificationsActiveOutlined as NotificationsActiveOutlinedIcon,
  PaidOutlined as PaidOutlinedIcon,
  PersonOutlineRounded as PersonOutlineRoundedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
  RouteOutlined as RouteOutlinedIcon,
  SensorsRounded as SensorsRoundedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  TaskAltOutlined as TaskAltOutlinedIcon,
} from "@mui/icons-material";
import {
  Activity,
  AlertTriangle,
  BellRing,
  BarChart3,
  Clock3,
  MapPin,
  RefreshCw,
  Search,
  ShieldAlert,
  Siren,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import GlobalDashboardSidebar from "../GlobalDashboardSidebar";
import useGlobalSidebarItems from "../useGlobalSidebar";
import { USER_ROLE_ROUTE_CONFIG } from "../../users/userRoleConfig";
import { logout } from "../../../store/authSlice";
import { useStatus } from "../../../popup/NotificationContext";
import {
  useAcknowledgeAlarmMutation,
  useGetAlarmsQuery,
  useResolveAlarmMutation,
} from "../../../redux/admin/alarmApi";
import { useGetAllLiteDevicesQuery } from "../../../redux/admin/adminApi";
import "../../admin/dashboard-redesign.css";
import "./NotificationPage.css";

const TASK_MENU_ITEMS = [
  { key: "all", label: "All Tasks" },
  { key: "create", label: "Create Task" },
  { key: "inventory", label: "Inventory" },
];

const MESSAGE_MENU_ITEMS = [
  { key: "compose", label: "Compose & Send" },
  { key: "templates", label: "Templates" },
  { key: "delivery-reports", label: "Delivery Reports" },
  { key: "scheduled", label: "Scheduled Messages" },
];

const EXPENSE_MENU_ITEMS = [
  { key: "create", label: "Create Expense", path: "/expense/create" },
  { key: "list", label: "Expense List", path: "/expense/list" },
  { key: "upload", label: "Upload Expense", path: "/expense/upload" },
];

const SENSOR_MENU_ITEMS = [
  { key: "list", label: "Sensor List", path: "/sensors/list" },
  { key: "create", label: "Add Sensor", path: "/sensors/create" },
  { key: "upload", label: "Upload Sensors", path: "/sensors/upload" },
];

const PAGE_SIZE = 12;
const CHART_COLORS = ["#245ee8", "#ef4444", "#f59e0b", "#10b981", "#8b5cf6", "#14b8a6"];
const SEVERITY_COLORS = {
  critical: "#b91c1c",
  high: "#ef4444",
  medium: "#f59e0b",
  warning: "#f97316",
  low: "#10b981",
  info: "#245ee8",
  neutral: "#94a3b8",
};

const safeLower = (value) => String(value || "").toLowerCase();

const formatLabel = (value) =>
  String(value || "unknown")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDateTime = (value) => {
  if (!value) return "No timestamp";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No timestamp";
  return date.toLocaleString("en-IN");
};

const normalizeAlarm = (item = {}, deviceMap = new Map()) => {
  const alarmId = String(item?._id || item?.alarmId || item?.id || "");
  const deviceId = String(item?.deviceId?._id || item?.deviceId || item?.device?._id || "");
  const device = deviceMap.get(deviceId);
  const latitude = Number(item?.location?.latitude ?? item?.location?.lat ?? item?.latitude ?? item?.lat);
  const longitude = Number(item?.location?.longitude ?? item?.location?.lng ?? item?.longitude ?? item?.lng);

  return {
    ...item,
    alarmId,
    deviceId,
    deviceName:
      item?.deviceName ||
      item?.device?.name ||
      device?.vehicleNumber ||
      device?.name ||
      item?.vehicleName ||
      "Unknown Vehicle",
    vehicleNumber: device?.vehicleNumber || item?.vehicleNumber || "",
    imei: item?.imei || item?.device?.imei || device?.imei || "",
    alarmType: item?.alarmType || item?.eventType || "unknown",
    severity: safeLower(item?.severity || item?.priority || "medium"),
    status: safeLower(item?.status || "active"),
    message:
      item?.message ||
      item?.description ||
      item?.notes ||
      item?.address ||
      item?.locationText ||
      "Alarm notification received from the device.",
    createdAt: item?.createdAt || item?.timestamp || item?.updatedAt || item?.triggeredAt || new Date().toISOString(),
    latitude: Number.isFinite(latitude) ? latitude : null,
    longitude: Number.isFinite(longitude) ? longitude : null,
  };
};

const severityClass = (value) => {
  const normalized = safeLower(value);
  if (normalized === "high" || normalized === "critical") return "danger";
  if (normalized === "medium" || normalized === "warning") return "warning";
  if (normalized === "low") return "success";
  return "neutral";
};

export default function NotificationsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state.auth.user);
  const { showStatus } = useStatus();

  const [search, setSearch] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [page, setPage] = useState(1);

  const { data: rawAlarms = [], isLoading, refetch, isFetching } = useGetAlarmsQuery({ limit: 300 });
  const { data: devices = [] } = useGetAllLiteDevicesQuery();
  const [acknowledgeAlarm, { isLoading: acknowledging }] = useAcknowledgeAlarmMutation();
  const [resolveAlarm, { isLoading: resolving }] = useResolveAlarmMutation();

  const profileName = currentUser?.name || currentUser?.username || "User";
  const roleLabel = String(currentUser?.role || "User")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const deviceMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(devices) ? devices : []).forEach((device) => {
      if (device?._id) map.set(String(device._id), device);
    });
    return map;
  }, [devices]);

  const alarms = useMemo(
    () => (Array.isArray(rawAlarms) ? rawAlarms : []).map((item) => normalizeAlarm(item, deviceMap)).filter((item) => item.alarmId),
    [deviceMap, rawAlarms]
  );

  const typeOptions = useMemo(() => {
    const uniqueTypes = [...new Set(alarms.map((item) => String(item.alarmType || "unknown")))];
    return ["all", ...uniqueTypes];
  }, [alarms]);

  const filteredAlarms = useMemo(() => {
    const query = safeLower(search).trim();
    return alarms.filter((alarm) => {
      const alarmType = safeLower(alarm.alarmType);
      const severity = safeLower(alarm.severity);
      const status = safeLower(alarm.status);

      if (severityFilter !== "all" && severity !== severityFilter) return false;
      if (statusFilter !== "all" && status !== statusFilter) return false;
      if (typeFilter !== "all" && alarmType !== safeLower(typeFilter)) return false;

      if (!query) return true;
      return (
        safeLower(alarm.deviceName).includes(query) ||
        safeLower(alarm.vehicleNumber).includes(query) ||
        safeLower(alarm.imei).includes(query) ||
        safeLower(alarm.message).includes(query) ||
        alarmType.includes(query)
      );
    });
  }, [alarms, search, severityFilter, statusFilter, typeFilter]);

  const alarmTrend = useMemo(() => {
    const days = Array.from({ length: 7 }, (_, index) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - index));
      const key = date.toISOString().slice(0, 10);
      return {
        key,
        label: date.toLocaleDateString("en-IN", { weekday: "short" }),
        total: 0,
      };
    });
    const byKey = new Map(days.map((day) => [day.key, day]));

    alarms.forEach((alarm) => {
      const key = new Date(alarm.createdAt).toISOString().slice(0, 10);
      const target = byKey.get(key);
      if (target) target.total += 1;
    });

    return days;
  }, [alarms]);

  const typeBreakdown = useMemo(() => {
    const counts = alarms.reduce((acc, item) => {
      const key = String(item.alarmType || "unknown");
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([key, value], index) => ({
        name: formatLabel(key),
        value,
        color: CHART_COLORS[index % CHART_COLORS.length],
      }));
  }, [alarms]);

  const severityBreakdown = useMemo(() => {
    const order = ["critical", "high", "medium", "warning", "low", "info"];
    const counts = alarms.reduce((acc, item) => {
      const key = safeLower(item.severity || "neutral");
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return order
      .filter((key) => counts[key] > 0)
      .map((key) => ({
        name: formatLabel(key),
        total: counts[key],
        fill: SEVERITY_COLORS[key] || SEVERITY_COLORS.neutral,
      }));
  }, [alarms]);

  const summary = useMemo(() => {
    const active = alarms.filter((item) => item.status === "active").length;
    const acknowledged = alarms.filter((item) => item.status === "acknowledged").length;
    const resolved = alarms.filter((item) => item.status === "resolved").length;
    const critical = alarms.filter((item) => ["high", "critical"].includes(item.severity)).length;

    return {
      total: alarms.length,
      active,
      acknowledged,
      resolved,
      critical,
      today: alarmTrend[alarmTrend.length - 1]?.total || 0,
    };
  }, [alarmTrend, alarms]);

  const trendInsights = useMemo(() => {
    const totals = alarmTrend.map((item) => item.total);
    const peak = Math.max(0, ...totals);
    const average = totals.length ? (totals.reduce((sum, value) => sum + value, 0) / totals.length).toFixed(1) : "0.0";
    return {
      peak,
      average,
    };
  }, [alarmTrend]);

  const pagedAlarms = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredAlarms.slice(start, start + PAGE_SIZE);
  }, [filteredAlarms, page]);

  const pageCount = Math.max(1, Math.ceil(filteredAlarms.length / PAGE_SIZE));

  const recentHighlights = useMemo(() => filteredAlarms.slice(0, 5), [filteredAlarms]);

  const sidebarItemsLegacy = useMemo(
    () => [
      { key: "dashboard", label: "Dashboard", icon: <AssessmentOutlinedIcon fontSize="small" />, active: location.pathname === "/dashboard", onClick: () => navigate("/dashboard") },
      { key: "vehicles", label: "Vehicles", icon: <DirectionsCarFilledOutlinedIcon fontSize="small" />, active: location.pathname === "/admin-dashboard" && location.search.includes("tab=devices"), onClick: () => navigate("/admin-dashboard?tab=devices") },
      { key: "live-tracking", label: "Live Tracking", icon: <PlaceOutlinedIcon fontSize="small" />, active: location.pathname === "/admin-dashboard" && location.search.includes("tab=map"), onClick: () => navigate("/admin-dashboard?tab=map") },
      { key: "trips", label: "Trips", icon: <RouteOutlinedIcon fontSize="small" />, active: location.pathname === "/logistic/trips", onClick: () => navigate("/logistic/trips") },
      { key: "geofences", label: "Geofences", icon: <PlaceOutlinedIcon fontSize="small" />, active: location.pathname === "/geofences", onClick: () => navigate("/geofences") },
      { key: "notifications", label: "Notifications", icon: <NotificationsActiveOutlinedIcon fontSize="small" />, active: location.pathname === "/notifications", onClick: () => navigate("/notifications") },
      { key: "reports", label: "Reports", icon: <ReceiptLongOutlinedIcon fontSize="small" />, active: location.pathname === "/reports", onClick: () => navigate("/reports") },
      {
        key: "tasks",
        label: "Tasks",
        icon: <TaskAltOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/tasks"),
        children: TASK_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === `/tasks/${item.key}`,
          onClick: () => navigate(`/tasks/${item.key}`),
        })),
      },
      {
        key: "messages",
        label: "Messages",
        icon: <MarkUnreadChatAltOutlinedIcon fontSize="small" />,
        active: location.pathname === "/message",
        children: MESSAGE_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === "/message" && (new URLSearchParams(location.search).get("section") || "compose") === item.key,
          onClick: () => navigate(`/message?section=${item.key}`),
        })),
      },
      { key: "groups", label: "Groups", icon: <Groups2OutlinedIcon fontSize="small" />, active: location.pathname.startsWith("/groups"), onClick: () => navigate("/groups") },
      { key: "reminders", label: "Reminders", icon: <EventNoteOutlinedIcon fontSize="small" />, active: location.pathname === "/reminders", onClick: () => navigate("/reminders") },
      {
        key: "expense",
        label: "Expense",
        icon: <PaidOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/expense"),
        children: EXPENSE_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      {
        key: "sensors",
        label: "Sensors",
        icon: <SensorsRoundedIcon fontSize="small" />,
        active: location.pathname.startsWith("/sensors"),
        children: SENSOR_MENU_ITEMS.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      {
        key: "logistic",
        label: "Logistic",
        icon: <LocalShippingOutlinedIcon fontSize="small" />,
        active: location.pathname.startsWith("/logistic"),
        children: [
          { key: "logistics-trips", label: "Trips", active: location.pathname === "/logistic/trips", onClick: () => navigate("/logistic/trips") },
          { key: "logistics-clients", label: "Clients", active: location.pathname === "/logistic/clients", onClick: () => navigate("/logistic/clients") },
          { key: "logistics-materials", label: "Materials", active: location.pathname === "/logistic/materials", onClick: () => navigate("/logistic/materials") },
          { key: "logistics-documents", label: "Documents", active: location.pathname === "/logistic/documents", onClick: () => navigate("/logistic/documents") },
        ],
      },
      {
        key: "users",
        label: "Users & Roles",
        icon: <PersonOutlineRoundedIcon fontSize="small" />,
        active: location.pathname.startsWith("/users"),
        children: USER_ROLE_ROUTE_CONFIG.map((item) => ({
          key: item.key,
          label: item.label,
          active: location.pathname === item.path,
          onClick: () => navigate(item.path),
        })),
      },
      { key: "settings", label: "Settings", icon: <SettingsOutlinedIcon fontSize="small" />, active: location.pathname === "/profile", onClick: () => navigate("/profile") },
    ],
    [location.pathname, location.search, navigate]
  );
  const sidebarItems = useGlobalSidebarItems();
  void sidebarItemsLegacy;

  const handleAcknowledge = async (alarmId) => {
    try {
      await acknowledgeAlarm({ alarmId, notes: "Acknowledged from notifications page" }).unwrap();
      showStatus("success", "Alarm acknowledged.");
      refetch();
    } catch (error) {
      showStatus("error", error?.data?.message || error?.data?.msg || "Unable to acknowledge alarm.");
    }
  };

  const handleResolve = async (alarmId) => {
    try {
      await resolveAlarm({ alarmId, notes: "Resolved from notifications page" }).unwrap();
      showStatus("success", "Alarm resolved.");
      refetch();
    } catch (error) {
      showStatus("error", error?.data?.message || error?.data?.msg || "Unable to resolve alarm.");
    }
  };

  const handleRefresh = async () => {
    try {
      await refetch();
      showStatus("success", "Notifications refreshed.");
    } catch {
      showStatus("error", "Unable to refresh notifications.");
    }
  };

  return (
    <div className="dashboard-page-shell">
      <GlobalDashboardSidebar
        items={sidebarItems}
        profileName={profileName}
        roleLabel={roleLabel}
        onSupport={() => navigate("/message")}
        onLogout={() => dispatch(logout())}
      />

      <main className="dashboard-content">
        <div className="notification-hub">
          <section className="notification-hero">
            <div className="notification-hero-card">
              <div className="notification-hero-kicker">
                <Siren size={14} />
                Device Alarm Center
              </div>
              <h1 className="notification-hero-title">Track every device alarm and notification from one responsive command page.</h1>
              <p className="notification-hero-copy">
                This page brings the old device sidebar notifications into a full workspace with filters, table view,
                device context, and action controls. Active alarms from devices, geofence entry and exit events,
                overspeed, ignition, power, and other alerts are visible here in one place.
              </p>
              <div className="notification-hero-actions">
                <button type="button" className="notification-primary-btn" onClick={handleRefresh} disabled={isFetching}>
                  <RefreshCw size={16} />
                  Refresh Feed
                </button>
                <button type="button" className="notification-secondary-btn" onClick={() => navigate("/admin-dashboard?tab=map")}>
                  <MapPin size={16} />
                  Open Live Tracking
                </button>
              </div>
            </div>

            <div className="notification-stat-grid">
              <div className="notification-stat-card">
                <div className="notification-stat-head">
                  <div className="notification-stat-icon"><BellRing size={18} /></div>
                  <span className="notification-stat-label">Total alarms</span>
                </div>
                <div className="notification-stat-value">{summary.total}</div>
                <div className="notification-stat-foot">All notifications pulled from device alarm feed</div>
              </div>
              <div className="notification-stat-card">
                <div className="notification-stat-head">
                  <div className="notification-stat-icon"><ShieldAlert size={18} /></div>
                  <span className="notification-stat-label">Active now</span>
                </div>
                <div className="notification-stat-value">{summary.active}</div>
                <div className="notification-stat-foot">Still waiting for operator acknowledgement</div>
              </div>
              <div className="notification-stat-card">
                <div className="notification-stat-head">
                  <div className="notification-stat-icon"><AlertTriangle size={18} /></div>
                  <span className="notification-stat-label">Critical</span>
                </div>
                <div className="notification-stat-value">{summary.critical}</div>
                <div className="notification-stat-foot">High priority device alerts</div>
              </div>
              <div className="notification-stat-card">
                <div className="notification-stat-head">
                  <div className="notification-stat-icon"><Activity size={18} /></div>
                  <span className="notification-stat-label">Today</span>
                </div>
                <div className="notification-stat-value">{summary.today}</div>
                <div className="notification-stat-foot">Alarms received today</div>
              </div>
            </div>
          </section>

          <section className="notification-main-grid">
            <div className="notification-panel">
              <div className="notification-panel-header">
                <div>
                  <h2 className="notification-panel-title">Recent Highlights</h2>
                  <p className="notification-panel-subtitle">Latest alarms that operators will usually check first.</p>
                </div>
              </div>

              <div className="notification-highlight-list">
                {isLoading ? (
                  <div className="notification-empty-state">Loading notifications…</div>
                ) : recentHighlights.length ? (
                  recentHighlights.map((alarm) => (
                    <div key={alarm.alarmId} className="notification-highlight-card">
                      <div className="notification-highlight-top">
                        <span className={`notification-badge ${severityClass(alarm.severity)}`}>{formatLabel(alarm.severity)}</span>
                        <span className="notification-badge neutral">{formatLabel(alarm.status)}</span>
                      </div>
                      <h3 className="notification-highlight-title">{formatLabel(alarm.alarmType)}</h3>
                      <p className="notification-highlight-copy">{alarm.deviceName}</p>
                      <div className="notification-highlight-meta">{formatDateTime(alarm.createdAt)}</div>
                    </div>
                  ))
                ) : (
                  <div className="notification-empty-state">No alarms available right now.</div>
                )}
              </div>
            </div>

            <div className="notification-panel notification-table-panel">
              <div className="notification-panel-header">
                <div>
                  <h2 className="notification-panel-title">All Device Notifications</h2>
                  <p className="notification-panel-subtitle">Search, filter, acknowledge, and resolve alarms in one table.</p>
                </div>
                <div className="notification-panel-tools">
                  <div className="notification-search-box">
                    <Search size={16} />
                    <input
                      value={search}
                      onChange={(event) => {
                        setSearch(event.target.value);
                        setPage(1);
                      }}
                      placeholder="Search by device, IMEI, type, or message"
                    />
                  </div>
                </div>
              </div>

              <div className="notification-filter-row">
                <select value={severityFilter} onChange={(event) => { setSeverityFilter(event.target.value); setPage(1); }}>
                  <option value="all">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="warning">Warning</option>
                  <option value="low">Low</option>
                  <option value="info">Info</option>
                </select>
                <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); setPage(1); }}>
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="resolved">Resolved</option>
                </select>
                <select value={typeFilter} onChange={(event) => { setTypeFilter(event.target.value); setPage(1); }}>
                  {typeOptions.map((option) => (
                    <option key={option} value={option}>{option === "all" ? "All Types" : formatLabel(option)}</option>
                  ))}
                </select>
              </div>

              <div className="notification-table-wrap">
                <table className="notification-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Device</th>
                      <th>Alarm</th>
                      <th>Severity</th>
                      <th>Status</th>
                      <th>Message</th>
                      <th>Location</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="8" className="notification-table-empty">Loading alarms…</td>
                      </tr>
                    ) : pagedAlarms.length ? (
                      pagedAlarms.map((alarm) => (
                        <tr key={alarm.alarmId}>
                          <td data-label="Time">
                            <div className="notification-table-time">
                              <Clock3 size={14} />
                              {formatDateTime(alarm.createdAt)}
                            </div>
                          </td>
                          <td data-label="Device">
                            <div className="notification-device-cell">
                              <strong>{alarm.deviceName}</strong>
                              <span>{alarm.vehicleNumber || alarm.imei || "No device code"}</span>
                            </div>
                          </td>
                          <td data-label="Alarm">{formatLabel(alarm.alarmType)}</td>
                          <td data-label="Severity">
                            <span className={`notification-badge ${severityClass(alarm.severity)}`}>{formatLabel(alarm.severity)}</span>
                          </td>
                          <td data-label="Status">
                            <span className={`notification-badge ${alarm.status === "active" ? "danger" : alarm.status === "resolved" ? "success" : "neutral"}`}>
                              {formatLabel(alarm.status)}
                            </span>
                          </td>
                          <td data-label="Message" className="notification-message-cell">{alarm.message}</td>
                          <td data-label="Location">
                            {alarm.latitude && alarm.longitude ? `${alarm.latitude.toFixed(5)}, ${alarm.longitude.toFixed(5)}` : "No coordinates"}
                          </td>
                          <td data-label="Action">
                            <div className="notification-action-row">
                              <button
                                type="button"
                                className="notification-row-btn"
                                onClick={() => handleAcknowledge(alarm.alarmId)}
                                disabled={acknowledging || alarm.status !== "active"}
                              >
                                Acknowledge
                              </button>
                              <button
                                type="button"
                                className="notification-row-btn ghost"
                                onClick={() => handleResolve(alarm.alarmId)}
                                disabled={resolving || alarm.status === "resolved"}
                              >
                                Resolve
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="notification-table-empty">No notifications match the current filters.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="notification-pagination">
                <span>
                  Showing {pagedAlarms.length ? (page - 1) * PAGE_SIZE + 1 : 0}-{Math.min(page * PAGE_SIZE, filteredAlarms.length)} of {filteredAlarms.length}
                </span>
                <div className="notification-pagination-actions">
                  <button type="button" onClick={() => setPage((value) => Math.max(1, value - 1))} disabled={page === 1}>
                    Prev
                  </button>
                  <span>Page {page} / {pageCount}</span>
                  <button type="button" onClick={() => setPage((value) => Math.min(pageCount, value + 1))} disabled={page === pageCount}>
                    Next
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="notification-chart-grid">
            <div className="notification-chart-card">
              <div className="notification-chart-head">
                <div>
                  <h3 className="notification-chart-title">7-Day Alarm Trend</h3>
                  <p className="notification-chart-copy">Quick trend of how many notifications devices are producing each day.</p>
                </div>
                <div className="notification-chip-row">
                  <span className="notification-badge neutral">Peak: {trendInsights.peak}</span>
                  <span className="notification-badge neutral">Avg/day: {trendInsights.average}</span>
                </div>
              </div>
              {alarmTrend.some((item) => item.total > 0) ? (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={alarmTrend}>
                    <defs>
                      <linearGradient id="notificationTrendFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#245ee8" stopOpacity={0.34} />
                        <stop offset="100%" stopColor="#245ee8" stopOpacity={0.04} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5efff" />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fill: "#94a3b8", fontSize: 12 }} allowDecimals={false} />
                    <Tooltip />
                    <Area type="monotone" dataKey="total" stroke="#245ee8" strokeWidth={3} fill="url(#notificationTrendFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="notification-empty-state">Trend graph will appear after alarms are received.</div>
              )}
            </div>

            <div className="notification-chart-card">
              <div className="notification-chart-head">
                <div>
                  <h3 className="notification-chart-title">Severity Mix</h3>
                  <p className="notification-chart-copy">See whether the notifications feed is mostly critical, warning, or informational.</p>
                </div>
                <div className="notification-chart-icon">
                  <BarChart3 size={18} />
                </div>
              </div>
              {severityBreakdown.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={severityBreakdown} layout="vertical" margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e5efff" />
                    <XAxis type="number" tickLine={false} axisLine={false} tick={{ fill: "#94a3b8", fontSize: 12 }} allowDecimals={false} />
                    <YAxis dataKey="name" type="category" tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 12 }} width={72} />
                    <Tooltip />
                    <Bar dataKey="total" radius={[0, 10, 10, 0]}>
                      {severityBreakdown.map((item) => (
                        <Cell key={item.name} fill={item.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="notification-empty-state">Severity graph will appear after alarms are received.</div>
              )}

              <div className="notification-chip-row">
                {severityBreakdown.map((item) => (
                  <span key={item.name} className="notification-badge neutral">
                    <span style={{ width: 8, height: 8, borderRadius: 999, background: item.fill, display: "inline-block" }} />
                    {item.name}: {item.total}
                  </span>
                ))}
              </div>
            </div>

            <div className="notification-chart-card">
              <div className="notification-chart-head">
                <div>
                  <h3 className="notification-chart-title">Top Alarm Types</h3>
                  <p className="notification-chart-copy">Most frequent alarm categories from the current notifications feed.</p>
                </div>
              </div>
              {typeBreakdown.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={typeBreakdown} dataKey="value" nameKey="name" innerRadius={58} outerRadius={92} paddingAngle={4}>
                      {typeBreakdown.map((item, index) => (
                        <Cell key={`${item.name}-${index}`} fill={item.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" height={12} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="notification-empty-state">Alarm distribution will appear here once data is available.</div>
              )}

              <div className="notification-chip-row">
                {typeBreakdown.map((item) => (
                  <span key={item.name} className="notification-badge neutral">
                    <span style={{ width: 8, height: 8, borderRadius: 999, background: item.color, display: "inline-block" }} />
                    {item.name}: {item.value}
                  </span>
                ))}
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
